import { graphql } from 'client/gql';
import { UpdateAbsenceMutationDocument } from 'components/Absences/forms/AbsenceForm';
import { AbsenceModal } from 'components/Absences/modals/AbsenceModal';
import { AbsenceAdministrationTable } from 'components/Absences/tables/AbsenceAdministrationTable';
import { BpSubpage } from 'components/BpSubpage/BpSubpage';
import dayjs from 'dayjs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useConfirm } from 'hooks/useConfirm';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showSuccessDeleteToast, showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { OrganizationConfigContext } from '../../../context/OrganizationConfigContext';

const DeleteAbsenceMutationDocument = graphql(`
  mutation BpDeleteAbsenceFromClassbookAdmin($where: AbsenceWhere!) {
    deleteAbsences(where: $where) {
      nodesDeleted
    }
  }
`);

const AbsencesAdministrationSubpagDocument = graphql(`
  query AbsencesAdministrationSubpagData($memberCoursesWhere: GroupWhere!, $managedClassesWhere: ClassWhere!) {
    memberCourses: groups(where: $memberCoursesWhere) {
      __typename
      uuid
      viewers {
        __typename
        uuid
      }
      editors {
        __typename
        uuid
      }
    }
    managedClasses: classes(where: $managedClassesWhere) {
      __typename
      uuid
    }
  }
`);

const AbsencesAdministrationAbsencesQuery = graphql(`
  query AbsencesAdministrationAbsencesData(
    $profilesWhere: ProfileWhere!
    $absencesWhere: AbsenceWhere!
    $classesWhere: ClassWhere!
  ) {
    ...AbsenceAdministrationTableFields
  }
`);

export const AbsencesAdministrationSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const childProfiles = pimAuthClaims.getChildProfiles().flatMap((cp) => cp.uuid);
  const organization = pimAuthClaims.getOrganization();
  const organizationUuid = organization.uuid;

  const [selectedAbsenceUuid, setSelectedAbsenceUuid] = useState<string | null>(null);
  const [absencesModalOpen, setAbsencesModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [fromDate, setFromDate] = useState<Date>(() => dayjs().startOf('day').toDate());
  const [untilDate, setUntilDate] = useState<Date>(() => dayjs().endOf('day').toDate());
  const { currentSchoolYear } = useContext(OrganizationConfigContext);

  const [, updateAbsence] = useMutation(UpdateAbsenceMutationDocument);
  const [, deleteAbsence] = useMutation(DeleteAbsenceMutationDocument);

  const [{ data }] = useQuery({
    query: AbsencesAdministrationSubpagDocument,
    variables: {
      memberCoursesWhere: {
        organization: { uuid: organizationUuid },
        schoolYear: { uuid: currentSchoolYear.uuid },
        OR: [
          { editorsConnection_SOME: { node: { uuid: profileUuid } } },
          { viewersConnection_SOME: { node: { uuid: profileUuid } } },
        ],
      },
      managedClassesWhere: {
        organization: { uuid: organizationUuid },
        schoolYear: { uuid: currentSchoolYear.uuid },
        tutors_SOME: { uuid: profileUuid },
      },
    },
  });

  const permissions = usePermissionChecker();

  const [{ data: absences }] = useQuery({
    query: AbsencesAdministrationAbsencesQuery,
    variables: {
      profile: profileUuid,
      absencesWhere: {
        startTime_LTE: untilDate.toISOString(),
        endTime_GTE: fromDate.toISOString(),
        ...(!permissions?.canViewAllAbsences()
          ? {
              OR: [
                {
                  ownerConnection: {
                    node: {
                      OR: [
                        { classes_SOME: { uuid_IN: data?.managedClasses.map((cls) => cls.uuid) } },
                        {
                          groupEditor_SOME: {
                            uuid_IN: data?.memberCourses.map((course) => course.uuid),
                          },
                        },
                        {
                          groupViewer_SOME: {
                            uuid_IN: data?.memberCourses.map((course) => course.uuid),
                          },
                        },
                        {
                          uuid_IN: [profileUuid, ...childProfiles],
                        },
                      ],
                    },
                  },
                },
              ],
            }
          : undefined),
      },
      profilesWhere: {
        organization: {
          uuid: organizationUuid,
        },
      },
      classesWhere: {
        schoolYear: {
          uuid: currentSchoolYear.uuid,
        },
        organization: {
          uuid: organizationUuid,
        },
      },
    },
  });

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('absences.title', { count: 1 }),
      context: 'female',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  function handleAdd() {
    setAbsencesModalOpen(true);
  }

  function handleEdit(uuid: string) {
    setSelectedAbsenceUuid(uuid);
    setAbsencesModalOpen(true);
  }

  async function handleExcuse(uuids: string[], isExcused: boolean) {
    setLoading(true);

    let errors = 0;

    for (const uuid of uuids) {
      const { error } = await updateAbsence({
        where: {
          uuid,
        },
        update: {
          excused: isExcused,
        },
      });
      if (error) errors++;
    }

    if (errors > 0) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }

    setLoading(false);
  }

  async function handleDelete(uuids: string[]) {
    const res = await confirmDelete();
    if (!res) return;

    setLoading(true);

    let errors = 0;

    for (const uuid of uuids) {
      const { error } = await deleteAbsence({ where: { uuid } });
      if (error) errors++;
    }

    if (errors > 0) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }

    setLoading(false);
    setSelectedAbsenceUuid(null);
  }

  return (
    <BpSubpage isForbidden={!permissions?.canViewAbsencesAdministration()}>
      <AbsenceAdministrationTable
        isLoading={loading}
        fromDate={fromDate}
        untilDate={untilDate}
        onFromChange={setFromDate}
        onUntilChange={setUntilDate}
        onAdd={() => handleAdd()}
        onEdit={(uuid) => handleEdit(uuid)}
        onExcuse={(uuids, isExcused) => handleExcuse(uuids, isExcused)}
        onDelete={(uuids) => handleDelete(uuids)}
        data={absences}
      />

      <AbsenceModal
        isOpen={absencesModalOpen}
        context={permissions?.canViewAllAbsences() ? 'admin' : 'teacher'}
        selectedAbsenceUuid={selectedAbsenceUuid}
        onClose={() => {
          setAbsencesModalOpen(false);
          setSelectedAbsenceUuid(null);
        }}
      />

      <ConfirmationDialog />
    </BpSubpage>
  );
};
